import { useCallback, useRef, useEffect } from 'react';

export const useScriptExecutor = () => {
  const workerRef = useRef(null);

  // Terminate the worker if it exists
  const terminateWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      terminateWorker();
    };
  }, [terminateWorker]);

  const executeScript = useCallback(async (scriptContent, context, type) => {
    return new Promise((resolve, reject) => {
      try {
        // Terminate any existing worker before starting a new one
        terminateWorker();

        // Create a new worker
        const worker = new Worker(new URL('./ScriptWorker.js', import.meta.url));
        workerRef.current = worker;

        // Set a hard timeout of 5 seconds to prevent infinite loops
        const timer = setTimeout(() => {
          terminateWorker();
          reject(new Error('Script execution timeout'));
        }, 5000);

        worker.onmessage = (e) => {
          clearTimeout(timer);
          resolve(e.data);
          terminateWorker(); // Clean up after successful execution
        };

        worker.onerror = (error) => {
          clearTimeout(timer);
          console.error('[SCRIPT-EXECUTOR] Worker error:', error);
          reject(new Error(error.message));
          terminateWorker();
        };

        // Post the message to start execution
        worker.postMessage({
          scriptContent,
          context,
          type
        });
      } catch (error) {
        console.error('Simple executor - execution failed:', error);
        reject(error);
      }
    });
  }, [terminateWorker]);

  return {
    executeScript,
    terminateWorker
  };
};
